import React from 'react';

import {
  Alert,
  Anchor,
  Button,
  Container,
  Group,
  List,
  Loader,
  Paper,
  Progress,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconArrowLeft } from '@tabler/icons-react';
import PageHeader from 'components/PageHeader';
import useOnboardingChecklist from 'hooks/gym/useOnboardingChecklist';
import useUpdateOnboardingStep from 'hooks/gym/useUpdateOnboardingStep';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { useNavigate } from 'react-router-dom';

import OnboardingChecklistCard from './OnboardingChecklistCard';
import {
  formatMinutesLeft,
  ONBOARDING_CATEGORIES,
  ONBOARDING_CATEGORY_ICONS,
  ONBOARDING_STEPS,
} from './steps';

const SUPPORT_EMAIL = 'hello@usekilo.com';

const CLOSING_ITEMS = [
  'Building your packages and billing plans',
  'Enabling packages for online sales',
  'Connecting your Kilo systems so they work together',
  'Applying memberships to athlete profiles',
];

const OnboardingChecklist = (): JSX.Element => {
  const navigate = useNavigate();
  const { trackOnboardingChecklistStepUpdated } = useStaffTracking();
  const { checkedByKey, completedCount, totalCount, gymId, isLoading, isError } = useOnboardingChecklist();
  const { mutate: updateStep, isLoading: isSaving, variables } = useUpdateOnboardingStep(gymId);

  // The count comes from the API and the estimates from `steps.ts`, so only steps the API
  // actually serves are summed. A step this build knows about but the API does not must not
  // add time to a figure the count never reflects.
  const minutesLeft = ONBOARDING_STEPS.filter(
    step => step.key in checkedByKey && !checkedByKey[step.key],
  ).reduce((total, step) => total + step.minutes, 0);

  const handleToggle = (stepKey: string, checked: boolean): void => {
    updateStep(
      { stepKey, checked },
      {
        onSuccess: () => trackOnboardingChecklistStepUpdated({ step_key: stepKey, checked }),
      },
    );
  };

  return (
    <>
      <PageHeader>
        <Group position='apart' align='center' noWrap>
          <Title order={1} size='h1'>
            Onboarding steps
          </Title>
        </Group>
      </PageHeader>

      <div style={{ overflowY: 'scroll' }}>
        <Container maw='980px' py='md'>
          <Stack spacing='md'>
            <Button
              variant='subtle'
              leftIcon={<IconArrowLeft size={16} />}
              onClick={() => navigate('/')}
              sx={{ alignSelf: 'flex-start' }}
              data-cy='onboardingChecklistBack'
            >
              Back to Dashboard
            </Button>

            <Group position='apart' align='flex-end' noWrap>
              <Text size='xs' color='dimmed' tt='uppercase' fw={700}>
                Get started with Kilo
              </Text>
              {!isLoading && !isError && (
                <Paper withBorder radius='xl' py={4} px='md' data-cy='onboardingChecklistProgressCount'>
                  <Text size='sm' color='dimmed'>
                    <Text span fw={700} color='kiloBlue'>
                      {completedCount}
                    </Text>{' '}
                    of {totalCount} complete · about {formatMinutesLeft(minutesLeft)} left
                  </Text>
                </Paper>
              )}
            </Group>

            <Paper withBorder radius='md' p='lg'>
              <Stack spacing='sm'>
                <Title order={2} size='h4'>
                  Welcome to Kilo. Let&apos;s get your gym set up.
                </Title>
                <Text>
                  These steps cover the setup to complete before your first working session. Most take three
                  minutes or less, and most gyms finish the whole list in about an hour.
                </Text>
                <Text>
                  Start with Account Settings. The rules you set there become the defaults for every class
                  and appointment you create later, so doing it first saves you from redoing that work. After
                  that, work through the rest in any order and check each one off as you finish. Your
                  progress is tracked here.
                </Text>
                <Text>
                  If you get stuck on something, email{' '}
                  <Anchor href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Anchor>. We&apos;ll be happy to
                  help make sure you&apos;re ready for your working session.
                </Text>
                {!isLoading && !isError && (
                  <Progress
                    value={totalCount > 0 ? (completedCount / totalCount) * 100 : 0}
                    size={8}
                    radius='xl'
                    mt='xs'
                    data-cy='onboardingChecklistPageProgress'
                  />
                )}
              </Stack>
            </Paper>

            {isLoading && <Loader />}
            {isError && (
              <Alert icon={<IconAlertCircle size={16} />} color='red' title='Unable to load your progress'>
                <Stack spacing='sm' align='flex-start'>
                  <Text size='sm'>Unable to load your progress. Please refresh and try again.</Text>
                  <Button size='xs' onClick={() => window.location.reload()}>
                    Try again
                  </Button>
                </Stack>
              </Alert>
            )}

            {!isLoading &&
              !isError &&
              ONBOARDING_CATEGORIES.map(category => {
                const steps = ONBOARDING_STEPS.filter(step => step.category === category);
                const CategoryIcon = ONBOARDING_CATEGORY_ICONS[category];

                return (
                  <Stack key={category} spacing='sm'>
                    <Group spacing='xs' align='center'>
                      <CategoryIcon size={20} />
                      <Title order={3} size='h4'>
                        {category}
                      </Title>
                    </Group>

                    {steps.map(step => (
                      <OnboardingChecklistCard
                        key={step.key}
                        step={step}
                        checked={!!checkedByKey[step.key]}
                        isSaving={isSaving && variables?.stepKey === step.key}
                        onToggle={checked => handleToggle(step.key, checked)}
                      />
                    ))}
                  </Stack>
                );
              })}

            {!isLoading && !isError && (
              <Paper withBorder radius='md' p='lg' data-cy='onboardingChecklistClosing'>
                <Stack spacing='sm'>
                  <Title order={2} size='h4'>
                    That&apos;s the groundwork done!
                  </Title>
                  <Text>
                    Everything above is what should be in place before your working sessions with a Kilo team
                    member. From here, the two of you will work through the rest together:
                  </Text>
                  <List spacing='xs'>
                    {CLOSING_ITEMS.map(item => (
                      <List.Item key={item}>{item}</List.Item>
                    ))}
                  </List>
                  <Text>
                    We look forward to meeting with you and supporting you through your move to Kilo Gym
                    Management.
                  </Text>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Container>
      </div>
    </>
  );
};

export default OnboardingChecklist;
